function normalizeFieldName(name) {
    return String(name || "").trim();
}
function isMdsField(field) {
    return String(field.tagName || "").toLowerCase().startsWith("mc-");
}
function readElementValue(field) {
    const value = field.value;
    return value === null || value === undefined ? "" : String(value);
}
function isSuccessfulField(field) {
    if (field.disabled) {
        return false;
    }
    const type = String(field.type || "").toLowerCase();
    if ((type === "checkbox" || type === "radio") && !field.checked) {
        return false;
    }
    return type !== "button" && type !== "submit" && type !== "reset" && type !== "file";
}
function queueMicrotaskCompat(callback) {
    if (typeof queueMicrotask === "function") {
        queueMicrotask(callback);
        return;
    }
    void Promise.resolve().then(callback);
}
function uniqueFields(fields) {
    return fields.filter((field, index) => fields.indexOf(field) === index);
}
export function createFormFieldAdapter(root) {
    function getNamedFields() {
        const fields = Array.from(root.querySelectorAll("[name]"));
        const formElements = "elements" in root ? root.elements : null;
        if (formElements && typeof formElements.length === "number") {
            for (let index = 0; index < formElements.length; index += 1) {
                const field = formElements.item(index);
                if (field && typeof field.getAttribute === "function" && field.getAttribute("name")) {
                    fields.push(field);
                }
            }
        }
        return uniqueFields(fields);
    }
    function candidatesFor(name) {
        const normalizedName = normalizeFieldName(name);
        if (!normalizedName) {
            return [];
        }
        return getNamedFields().filter(field => String(field.name || field.getAttribute("name") || "") === normalizedName);
    }
    function getField(name) {
        const candidates = candidatesFor(name);
        return candidates.find(isMdsField) ?? candidates[0] ?? null;
    }
    function readFieldValue(name) {
        const field = getField(name);
        return field ? readElementValue(field) : "";
    }
    function writeFieldValue(name, value) {
        const field = getField(name);
        if (!field) {
            return;
        }
        field.value = value === null || value === undefined ? "" : String(value);
    }
    function onFieldInput(name, handler) {
        const field = getField(name);
        if (!field) {
            return () => undefined;
        }
        const eventNames = ["input", "change", "optionselected"];
        let lastValue;
        let resetQueued = false;
        const listener = (event) => {
            const currentValue = readElementValue(field);
            if (lastValue === currentValue) {
                return;
            }
            lastValue = currentValue;
            if (!resetQueued) {
                resetQueued = true;
                queueMicrotaskCompat(() => {
                    lastValue = undefined;
                    resetQueued = false;
                });
            }
            handler(event, field);
        };
        eventNames.forEach(eventName => field.addEventListener(eventName, listener));
        return () => {
            eventNames.forEach(eventName => field.removeEventListener(eventName, listener));
        };
    }
    function setFieldInvalid(name, invalidOrMessage = false, message = "") {
        const field = getField(name);
        if (!field) {
            return;
        }
        const isInvalid = typeof invalidOrMessage === "string" || invalidOrMessage === true;
        const errorMessage = typeof invalidOrMessage === "string" ? invalidOrMessage : message;
        field.classList.toggle("invalid", isInvalid);
        const label = field.closest("label");
        label?.classList.toggle("invalid-label", isInvalid);
        if (isMdsField(field)) {
            field.invalid = isInvalid;
            field.errormessage = isInvalid ? errorMessage : "";
        }
        else {
            field.setCustomValidity?.(isInvalid ? errorMessage : "");
        }
        if (isInvalid) {
            field.setAttribute("aria-invalid", "true");
        }
        else {
            field.removeAttribute("aria-invalid");
        }
    }
    function focusField(name) {
        const field = getField(name);
        if (!field) {
            return;
        }
        if (typeof field.focus === "function") {
            field.focus();
            return;
        }
        field.inputElement?.focus();
    }
    function serializeForm() {
        const grouped = new Map();
        getNamedFields().forEach(field => {
            const name = normalizeFieldName(String(field.name || field.getAttribute("name") || ""));
            if (!name) {
                return;
            }
            const fields = grouped.get(name) ?? [];
            fields.push(field);
            grouped.set(name, fields);
        });
        const serialized = {};
        grouped.forEach((fields, name) => {
            const logicalFields = fields.some(isMdsField) ? fields.filter(isMdsField) : fields;
            const values = logicalFields.filter(isSuccessfulField).map(readElementValue);
            if (values.length === 1) {
                serialized[name] = values[0];
            }
            else if (values.length > 1) {
                serialized[name] = values;
            }
        });
        return serialized;
    }
    return {
        getField,
        readFieldValue,
        writeFieldValue,
        onFieldInput,
        setFieldInvalid,
        focusField,
        serializeForm
    };
}
//# sourceMappingURL=form-field-adapter.js.map